import { ref, readonly } from 'vue';

/**
 * Global session status tracking composable
 * Tracks when the application encounters authentication issues (401 errors)
 * Similar to useServerStatus but focused on auth/session issues
 */

// Global reactive state - shared across all instances
const hasSessionExpired = ref(false);
const lastSessionError = ref<Date | null>(null);
const isHandling401 = ref(false); // Prevent multiple concurrent 401 handlers

export function useSessionStatus() {
    /**
     * Report a session expiration (401 error)
     * This will trigger user notification and cleanup
     */
    const reportSessionExpired = async (): Promise<void> => {
        if (hasSessionExpired.value) {
            // Already handling session expiration
            return;
        }
        
        // Check if user is currently logging in or out - don't mark session as expired
        try {
            // Dynamic import to avoid circular dependency
            const { useAuthStore } = await import('@/stores/auth');
            const authStore = useAuthStore();
            
            if (authStore.isLoggingOut) {
                return;
            }
            
            if (authStore.isLoggingIn) {
                return;
            }
            
            if (!authStore.isAuthenticated) {
                return;
            }
        } catch (error) {
            // If we can't check auth state, continue with normal handling
            console.error('Could not check auth state in reportSessionExpired:', error);
        }

        hasSessionExpired.value = true;
        lastSessionError.value = new Date();
    };

    /**
     * Clear the session error state
     * Called after successful login or re-authentication
     */
    const clearSessionError = (): void => {
        if (hasSessionExpired.value) {
            console.info('✅ Session error status cleared');
        }
        
        hasSessionExpired.value = false;
        lastSessionError.value = null;
    };

    /**
     * Check if we're currently handling a 401 error
     * This prevents multiple concurrent 401 handlers from running
     */
    const startHandling401 = (): boolean => {
        if (isHandling401.value) {
            return false; // Already handling
        }
        isHandling401.value = true;
        return true;
    };

    /**
     * Mark 401 handling as complete
     */
    const finishHandling401 = (): void => {
        isHandling401.value = false;
    };

    /**
     * Report a successful API response with authentication
     * This automatically clears any existing session error state
     */
    const reportAuthSuccess = (): void => {
        if (hasSessionExpired.value) {
            clearSessionError();
        }
    };

    return {
        // State
        hasSessionExpired: readonly(hasSessionExpired),
        lastSessionError: readonly(lastSessionError),
        isHandling401: readonly(isHandling401),
        
        // Actions
        reportSessionExpired,
        clearSessionError,
        startHandling401,
        finishHandling401,
        reportAuthSuccess
    };
}

